import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Title from '../../../components/Title';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Divider, Row } from 'antd';
import CustomInput from '../../../components/CustomInput';
import CustomSelect from '../../../components/CustomSelect';
import MultinationalTabs from '../../../components/MultinationalTabs';
import FAQEditors from './FAQEditors';
import CustomMultiselect from '../../../components/CustomMultiselect';
import { capitalize, createShallowCopy } from '../../../utils/helpFunctions';
import {
    availableProducts,
    getAvailableProducts,
} from '../../../redux/slices/productsSlice';
import {
    allFaqsGroups,
    createFaq,
    editFaq,
    getFaqById,
    getFaqsGroups,
    oneFaq,
    resetFaq,
} from '../../../redux/slices/faqSlice';
import { toast } from 'react-toastify';
import LocalLoader from '../../../components/LocalLoader';

export default function CreateFAQPage() {
    const dispatch = useDispatch();
    const location = useLocation();
    const params = useParams();
    const navigate = useNavigate();

    const products = useSelector(availableProducts);
    const faq = useSelector(oneFaq);
    const groups = useSelector(allFaqsGroups);

    const countriesLocalizationsData = useMemo(
        () =>
            faq?.details?.reduce((acc, el) => {
                acc[el.lang_code.toLowerCase()] = el;
                return acc;
            }, {}),
        [faq?.details],
    );

    const selectCountriesOptions = useMemo(
        () => [
            { code: 'SE', value: 'SE', label: 'SE' },
            { code: 'FI', value: 'FI', label: 'FI' },
            { code: 'DK', value: 'DK', label: 'DK' },
            { code: 'NO', value: 'NO', label: 'NO' },
            { code: 'IS', value: 'IS', label: 'IS' },
        ],
        [],
    );

    const selectGroupOptions = useMemo(
        () =>
            groups?.map((el) => {
                const foundGroupNames =
                    el?.names?.find(
                        (el) => el.lang_code === 'en' && !!el.name,
                    ) || el?.names?.find((el) => !!el.name);

                return {
                    ...el,
                    value: foundGroupNames?.name,
                    label:
                        foundGroupNames?.name.length > 50
                            ? foundGroupNames?.name.slice(50) + '...'
                            : foundGroupNames?.name,
                };
            }),
        [groups],
    );

    const selectAccountTypeOptions = useMemo(
        () => [
            {
                has_b2b_owner: true,
                value: 'has_b2b_owner',
                label: 'B2B account owner',
            },
            {
                has_b2b_sub: true,
                value: 'has_b2b_sub',
                label: 'B2B sub account',
            },
            {
                has_b2c_owner: true,
                value: 'has_b2c_owner',
                label: 'B2C account owner',
            },
            {
                has_b2c_sub: true,
                value: 'has_b2c_sub',
                label: 'B2C sub account',
            },
        ],
        [],
    );

    const selectProductsOptions = useMemo(
        () =>
            products?.map((el) => ({
                title: el.title,
                value: el.title,
                label: capitalize(el.title.toLowerCase().split('_').join(' ')),
            })),
        [products],
    );

    const [action, setAction] = useState('create');
    const [errors, setErrors] = useState({});
    const [isStateLoaded, setIsStateLoaded] = useState(false);

    const [data, setData] = useState({
        group: null,
        countries: [],
        account_types: [],
        products: [],
        details: {
            sv: {
                title: '',
                content: '',
                info_boxes: [],
            },
            no: {
                title: '',
                content: '',
                info_boxes: [],
            },
            fi: {
                title: '',
                content: '',
                info_boxes: [],
            },
            da: {
                title: '',
                content: '',
                info_boxes: [],
            },
            en: {
                title: '',
                content: '',
                info_boxes: [],
            },
            is: {
                title: '',
                content: '',
                info_boxes: [],
            },
        },
    });

    const fetchData = useCallback(() => {
        dispatch(getAvailableProducts()).unwrap();
        dispatch(getFaqsGroups()).unwrap();
    }, [dispatch]);

    useEffect(() => {
        fetchData();
        if (location?.pathname?.includes('edit')) {
            setAction('edit');
            dispatch(getFaqById(params?.id));
        }
    }, [dispatch, fetchData, location, params]);

    useEffect(() => {
        const faqCountries = faq?.countries?.map((el) => ({
            code: el.code,
            value: el.code,
            label: el.code,
        }));

        const faqProducts = faq?.products?.map((el) => ({
            title: el.title,
            value: el.title,
            label: capitalize(el.title.toLowerCase().split('_').join(' ')),
        }));

        const accountTypes = selectAccountTypeOptions.reduce((acc, el) => {
            if (faq?.account_types?.[el.value]) {
                acc.push({
                    [el.value]: true,
                    value: el.value,
                    label: el.label,
                });
            }
            return acc;
        }, []);

        setData({
            group: faq?.group || null,
            countries: faqCountries || selectCountriesOptions,
            account_types: !!accountTypes.length
                ? accountTypes
                : selectAccountTypeOptions,
            products: faqProducts || selectProductsOptions,
            details: {
                sv: {
                    title: countriesLocalizationsData?.sv?.title || '',
                    content: countriesLocalizationsData?.sv?.content || '',
                    info_boxes:
                        countriesLocalizationsData?.sv?.info_boxes || [],
                },
                no: {
                    title: countriesLocalizationsData?.no?.title || '',
                    content: countriesLocalizationsData?.no?.content || '',
                    info_boxes:
                        countriesLocalizationsData?.no?.info_boxes || [],
                },
                fi: {
                    title: countriesLocalizationsData?.fi?.title || '',
                    content: countriesLocalizationsData?.fi?.content || '',
                    info_boxes:
                        countriesLocalizationsData?.fi?.info_boxes || [],
                },
                da: {
                    title: countriesLocalizationsData?.da?.title || '',
                    content: countriesLocalizationsData?.da?.content || '',
                    info_boxes:
                        countriesLocalizationsData?.da?.info_boxes || [],
                },
                en: {
                    title: countriesLocalizationsData?.en?.title || '',
                    content: countriesLocalizationsData?.en?.content || '',
                    info_boxes:
                        countriesLocalizationsData?.en?.info_boxes || [],
                },
                is: {
                    title: countriesLocalizationsData?.is?.title || '',
                    content: countriesLocalizationsData?.is?.content || '',
                    info_boxes:
                        countriesLocalizationsData?.is?.info_boxes || [],
                },
            },
        });

        if (location?.pathname?.includes('create') || faq?.id) {
            setIsStateLoaded(true);
        }
    }, [
        countriesLocalizationsData,
        faq,
        location?.pathname,
        selectAccountTypeOptions,
        selectCountriesOptions,
        selectProductsOptions,
    ]);

    useEffect(() => {
        return () => dispatch(resetFaq());
    }, [dispatch]);

    const onInputChange = useCallback(
        (e, flag) => {
            const { value } = e.target;
            const dataCopy = createShallowCopy(data);

            dataCopy.details[flag].title = value;

            setData(dataCopy);
        },
        [data],
    );

    const onSelectChange = useCallback(
        (option, selectName) => {
            setData({ ...data, [selectName]: option });
        },
        [data],
    );

    const onMultiselectChange = useCallback(
        (options, selectName) => {
            setData({ ...data, [selectName]: options });
        },
        [data],
    );

    const onChangeMultinational = useCallback(
        (text, flag) => {
            const dataCopy = createShallowCopy(data);
            dataCopy.details[flag].content = text;

            setData(dataCopy);
        },
        [data],
    );

    const onAddInfoBox = useCallback(
        (flag) => {
            const dataCopy = createShallowCopy(data);

            dataCopy.details[flag].info_boxes.push({
                content: '',
                title: '',
            });

            setData(dataCopy);
        },
        [data],
    );

    const onChangeInfoBox = useCallback(
        ({ text, flag, id, field }) => {
            const dataCopy = createShallowCopy(data);
            const infoBoxIndex = dataCopy.details[flag].info_boxes.findIndex(
                (el) => el.id === id,
            );
            const infoBox = dataCopy.details[flag].info_boxes[infoBoxIndex];

            infoBox[field] = text;

            setData(dataCopy);
        },
        [data],
    );

    const onRemoveInfoBox = useCallback(
        (flag, id) => {
            const dataCopy = createShallowCopy(data);

            const filteredInfoBoxes = dataCopy.details[flag].info_boxes.filter(
                (el) => el.id !== id,
            );
            dataCopy.details[flag].info_boxes = filteredInfoBoxes;

            setData(dataCopy);
        },
        [data],
    );

    const onCancelButtonClick = useCallback(
        (e) => {
            e.preventDefault();
            navigate(-1);
        },
        [navigate],
    );

    const createRequestDetailsData = useCallback(
        (data) =>
            Object.entries(data.details).map((el) => ({
                lang_code: el[0].toUpperCase(),
                ...el[1],
            })),
        [],
    );

    const onCreatePackage = useCallback(
        async (e) => {
            e.preventDefault();

            const requestDetailsData = createRequestDetailsData(data);
            const request = {
                ...data,
                details: requestDetailsData,
            };

            try {
                await dispatch(createFaq(request)).unwrap();
                toast.success('New question successfully created');
                navigate('../faq');
            } catch (error) {
                setErrors(error.errors);
            }
        },
        [createRequestDetailsData, data, dispatch, navigate],
    );

    const onUpdatePackage = useCallback(
        async (e) => {
            e.preventDefault();

            const requestDetailsData = createRequestDetailsData(data);
            const request = {
                ...data,
                details: requestDetailsData,
            };

            try {
                await dispatch(
                    editFaq({ body: request, id: params.id }),
                ).unwrap();
                toast.success('Changes saved successfully');
                navigate('../faq');
            } catch (error) {
                setErrors(error.errors);
            }
        },
        [createRequestDetailsData, data, dispatch, navigate, params.id],
    );

    return (
        <div>
            <Title subText="Please fill information below">
                {action === 'create' ? 'Add new question' : 'Edit question'}
            </Title>
            <form>
                <Row>
                    <Col span={10}>
                        <div className="form__item-wrapper">
                            <MultinationalTabs
                                renderWithFlag={(flag) => (
                                    <CustomInput
                                        id={`title_${flag}`}
                                        placeholder="Enter question"
                                        onInputChange={(e) =>
                                            onInputChange(e, flag)
                                        }
                                        inputValue={
                                            data?.details?.[flag]?.title
                                        }
                                        labelText="Question"
                                        width="100%"
                                        errorText={errors?.details}
                                    />
                                )}
                            />
                        </div>
                        <Divider />
                        <div className="form__item-wrapper">
                            <p className="input-label">Group</p>
                            <CustomSelect
                                name="group_name"
                                onChange={(option) =>
                                    onSelectChange(option, 'group')
                                }
                                options={selectGroupOptions}
                                placeholder="Select group"
                                value={data?.group?.id}
                                defaultValue={null}
                                errorText={errors?.group}
                            />
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Countries</p>
                            <CustomMultiselect
                                name="countries"
                                onChange={(option) =>
                                    onMultiselectChange(option, 'countries')
                                }
                                options={selectCountriesOptions}
                                placeholder="Select countries"
                                value={data?.countries}
                                errorText={errors?.countries}
                                hideSelectedOptions={false}
                                closeMenuOnSelect={false}
                            />
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Account type</p>
                            <CustomMultiselect
                                name="account_types"
                                onChange={(option) =>
                                    onMultiselectChange(option, 'account_types')
                                }
                                options={selectAccountTypeOptions}
                                placeholder="Select account types"
                                value={data?.account_types}
                                errorText={errors?.account_types}
                                hideSelectedOptions={false}
                                closeMenuOnSelect={false}
                            />
                        </div>
                        <div className="form__item-wrapper">
                            <p className="input-label">Activated products</p>
                            <CustomMultiselect
                                name="products"
                                onChange={(option) =>
                                    onMultiselectChange(option, 'products')
                                }
                                options={selectProductsOptions}
                                placeholder="Select products"
                                value={data?.products}
                                errorText={errors?.products}
                                hideSelectedOptions={false}
                                closeMenuOnSelect={false}
                            />
                        </div>
                        <Divider />
                    </Col>
                </Row>
                <Row>
                    <Col span={20}>
                        <LocalLoader isLoading={!isStateLoaded}>
                            <MultinationalTabs>
                                <FAQEditors
                                    data={data?.details}
                                    onChange={onChangeMultinational}
                                    onAddInfoBox={onAddInfoBox}
                                    onRemoveInfoBox={onRemoveInfoBox}
                                    onChangeInfoBox={onChangeInfoBox}
                                    errors={errors}
                                />
                            </MultinationalTabs>
                        </LocalLoader>
                    </Col>
                </Row>
                <Row>
                    <Col span={10}>
                        <div className="form__footer">
                            <button
                                className="modal__button modal__button-cancel"
                                onClick={onCancelButtonClick}
                            >
                                Cancel
                            </button>
                            <button
                                className="modal__button modal__button-accept"
                                onClick={
                                    action === 'edit'
                                        ? onUpdatePackage
                                        : onCreatePackage
                                }
                            >
                                {action === 'edit' ? 'Update' : 'Add'}
                            </button>
                        </div>
                    </Col>
                </Row>
            </form>
        </div>
    );
}
